import { DestroyRef, Directive, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroupDirective } from '@angular/forms';
import { merge } from 'rxjs';
import { CurrentUserState } from '../state/current-user.state';
import { getCompanyTabAccessType } from '../utilities/companyTabAccess';

/**
 * Disables the host element's FormGroup when the current user holds the given company tab
 * read-only, so a read-only tab still renders its data but none of it can be typed over.
 *
 * The disable is re-applied on every value/status change: tabs rebuild their FormArrays with
 * `setControl` on each year change, and a FormGroup counts as enabled again the moment one
 * enabled child is added back to it.
 *
 * Usage — on the element that already carries [formGroup]:
 *   <div [formGroup]="form" [appReadOnlyTab]="'board'">
 */
@Directive({
  selector: '[appReadOnlyTab][formGroup]',
  standalone: true
})
export class ReadOnlyTabDirective implements OnInit {
  private currentUserState = inject(CurrentUserState);
  private formGroupDirective = inject(FormGroupDirective, { self: true });
  private destroyRef = inject(DestroyRef);

  /** Company-form tab id, e.g. 'board'. Must be a key of COMPANY_TAB_ACCESS_TYPES. */
  appReadOnlyTab = input.required<string>();

  ngOnInit(): void {
    const accessType = getCompanyTabAccessType(this.appReadOnlyTab());
    if (accessType === null || !this.currentUserState.isCompanyTabReadOnly(accessType)) {
      return;
    }

    const form = this.formGroupDirective.form;

    // emitEvent: false — re-emitting here would feed straight back into this subscription.
    const disable = () => {
      if (form.enabled) {
        form.disable({ emitEvent: false });
      }
    };

    disable();

    merge(form.valueChanges, form.statusChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(disable);
  }
}
